import React, { useEffect, useRef, useState } from "react";
import { useAudioSystem } from "../context/AudioContext";
import { Play, Pause, Music2, WifiOff } from "lucide-react";

let scriptLoaded = false;

export default function MusicSection({ playlists }) {
  const { registerSpotify, reportMusicState, status, online } = useAudioSystem();
  const controllerRef = useRef(null);
  const embedRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    if (playlists && playlists.length && !current) setCurrent(playlists[0]);
  }, [playlists, current]);

  useEffect(() => {
    registerSpotify({
      pause: () => { try { controllerRef.current?.pause(); } catch (e) {} },
      resume: () => { try { controllerRef.current?.resume(); } catch (e) {} },
    });
  }, [registerSpotify]);

  useEffect(() => {
    if (!current) return;
    const uri = `spotify:playlist:${current.spotify_id}`;

    const buildController = (IFrameAPI) => {
      if (!embedRef.current) return;
      if (controllerRef.current) {
        try { controllerRef.current.loadUri(uri); } catch (e) {}
        reportMusicState(false, { name: current.name });
        return;
      }
      IFrameAPI.createController(embedRef.current, { uri, width: "100%", height: "352" }, (controller) => {
        controllerRef.current = controller;
        setReady(true);
        controller.addListener("playback_update", (e) => {
          const paused = e?.data?.isPaused;
          reportMusicState(paused === false, { name: current.name });
        });
      });
    };

    if (window.SpotifyIframeApi) {
      buildController(window.SpotifyIframeApi);
    } else {
      window.onSpotifyIframeApiReady = (IFrameAPI) => {
        window.SpotifyIframeApi = IFrameAPI;
        buildController(IFrameAPI);
      };
      if (!scriptLoaded) {
        scriptLoaded = true;
        const s = document.createElement("script");
        s.src = "https://open.spotify.com/embed/iframe-api/v1";
        s.async = true;
        document.body.appendChild(s);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const play = () => { try { controllerRef.current?.resume(); } catch (e) {} };
  const pause = () => { try { controllerRef.current?.pause(); } catch (e) {} };

  const disabled = status === "priority";

  return (
    <div className="bg-[#18181B] border border-white/10 rounded-2xl p-5 flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-black text-2xl tracking-tight text-[#FF007F] neon-glow-pink flex items-center gap-2">
          <Music2 className="w-7 h-7" /> MUSIC
        </h2>
        {!online && (
          <span className="flex items-center gap-2 text-[#FFCC00] font-bold text-sm">
            <WifiOff className="w-5 h-5" /> SPOTIFY UNAVAILABLE
          </span>
        )}
      </div>

      {/* Playlist selector */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {(playlists || []).map((p) => (
          <button
            key={p.id}
            data-testid={`playlist-${p.id}`}
            onClick={() => setCurrent(p)}
            className={`h-16 rounded-xl font-display font-bold text-base tracking-wide active:scale-95 transition-transform duration-75 border ${
              current?.id === p.id
                ? "bg-[#FF007F] text-white border-transparent"
                : "bg-[#27272A] text-white border-white/10 hover:bg-[#3f3f46]"
            }`}
          >{p.name.toUpperCase()}</button>
        ))}
      </div>

      {/* Embed shows now-playing track + album art + inline controls */}
      <div className="rounded-xl overflow-hidden bg-black mb-4 min-h-[152px]">
        {online ? (
          <div ref={embedRef} data-testid="spotify-embed" />
        ) : (
          <div className="h-[152px] flex items-center justify-center text-[#A1A1AA] font-bold text-center px-4">
            Spotify unavailable — priority & emergency audio still work.
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mt-auto">
        <button
          data-testid="music-play"
          onClick={play}
          disabled={disabled || !ready}
          className="h-20 rounded-xl bg-[#00FA9A] text-black font-display font-black text-xl tracking-wide flex items-center justify-center gap-2 active:scale-95 transition-transform duration-75 disabled:opacity-40"
        ><Play className="w-8 h-8" /> PLAY</button>
        <button
          data-testid="music-pause"
          onClick={pause}
          disabled={disabled || !ready}
          className="h-20 rounded-xl bg-[#27272A] text-white font-display font-black text-xl tracking-wide flex items-center justify-center gap-2 active:scale-95 transition-transform duration-75 border border-white/10 disabled:opacity-40"
        ><Pause className="w-8 h-8" /> PAUSE</button>
      </div>
      <p className="text-[#71717A] text-xs mt-3 leading-relaxed">
        Skip / previous / volume are available on the embedded Spotify player and on the venue Spotify device.
        Master volume below controls priority &amp; emergency audio.
      </p>
    </div>
  );
}
